import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SCENES } from "@/components/tour/scenes";
import { useNarration } from "@/components/tour/useNarration";
import {
  Play, Pause, Volume2, VolumeX, Maximize2, Minimize2, X, Subtitles, Loader2, RotateCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MILESTONES = [[0.25, "progress_25"], [0.5, "progress_50"], [0.75, "progress_75"]];

const clock = (s) => {
  if (!s || Number.isNaN(s)) return "0:00";
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const trackTour = (event, at_seconds) => {
  try {
    let session = sessionStorage.getItem("tour_session");
    if (!session) {
      session = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("tour_session", session);
    }
    const body = JSON.stringify({ event, session, at_seconds: at_seconds ?? null });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/public/tour-events`,
        new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${API}/public/tour-events`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true })
        .catch(() => {});
    }
  } catch { /* analytics must never break playback */ }
};

/* Bumped whenever the narration is rebuilt so browsers and home-screen installs never play a
   stale audio file against a fresh cue sheet (that mismatch looks like the captions are "off"). */
const TOUR_VERSION = "20260622-1";

/* The tour is composed on a canvas 900 design-units tall whose width matches the screen it is
   playing on, then scaled to fit exactly. That way it fills every display edge to edge — no
   letterbox bars, no cropping — while every scene keeps its proportions. */
const FRAME_H = 900;
const MIN_ASPECT = 1.2;
const MAX_ASPECT = 2.8;

export default function PlatformTour({ onClose, onStart, onExplore }) {
  const shellRef = useRef(null);
  const stageRef = useRef(null);
  const firedRef = useRef(new Set());
  const [data, setData] = useState(null);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [full, setFull] = useState(false);
  const [cinema, setCinema] = useState(false);
  const [failed, setFailed] = useState(false);
  const [scale, setScale] = useState(1);
  const [frame, setFrame] = useState({ w: 1600, h: FRAME_H });
  const [rotated, setRotated] = useState(false);
  const [box, setBox] = useState({ w: 0, h: 0 });
  /* Once the viewer has pressed play the opening card never comes back. A dropped audio resource
     resets currentTime to 0, and without this that read as "time === 0" and threw the giant
     splash overlay back over a film that was four minutes in. */
  const [started, setStarted] = useState(false);

  /* A portrait phone is 9:16 while the film is 16:9, so upright it could only ever be a narrow
     band. Instead the whole player rotates a quarter turn and fills the screen edge to edge —
     the viewer turns the phone sideways and gets the full, legible picture, exactly like a video
     app going fullscreen. Desktops and landscape devices are untouched. */
  useEffect(() => {
    const measure = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Any portrait phone or tablet turns a quarter turn: a 16:9-ish film cannot fill a portrait
      // screen without either bars or cropping, and rotating avoids both.
      const portrait = h > w && w < 1100;
      setRotated(portrait);
      setBox(portrait ? { w: h, h: w } : { w, h });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);

  /* The canvas takes the shape of the screen it is playing on, so the picture fills the display
     completely — nothing letterboxed, nothing cut off. */
  useEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    const fit = () => {
      // offsetWidth/Height, not getBoundingClientRect: the player may itself be rotated, and we
      // need the untransformed layout box.
      const width = node.offsetWidth;
      const height = node.offsetHeight;
      if (!width || !height) return;
      const aspect = Math.min(Math.max(width / height, MIN_ASPECT), MAX_ASPECT);
      const w = Math.round(FRAME_H * aspect);
      setFrame({ w, h: FRAME_H });
      setScale(Math.max(width / w, height / FRAME_H));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(node);
    window.addEventListener("orientationchange", fit);
    return () => { ro.disconnect(); window.removeEventListener("orientationchange", fit); };
  }, [data, rotated, box.w, box.h]);

  useEffect(() => {
    const sync = () => setFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  useEffect(() => {
    fetch(`/tour/script.json?v=${TOUR_VERSION}`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        setData({ ...d, cues: d.lines.map((l, i) => ({ ...l, i })),
                  lines: d.lines.map((l) => ({ ...l, file: `/tour/${l.file}?v=${TOUR_VERSION}` })) });
        trackTour("loaded");
      })
      .catch(() => setFailed(true));
  }, []);

  const lines = data?.lines;
  const { index: cueIndex, playing, speaking, elapsed, total, recovering,
          play: startNarration, pause: pauseNarration, seek: seekNarration,
          setVolume: setClipVolume } = useNarration(lines, {
    master: data ? `/tour/${data.master || "narration.mp3"}?v=${TOUR_VERSION}` : null,
    duration: data?.duration,
    onEnded: () => {
      if (!firedRef.current.has("completed")) {
        firedRef.current.add("completed");
        trackTour("completed", 100);
      }
    },
  });

  const duration = total || data?.duration || 0;
  const time = elapsed;
  const cue = data ? data.cues[cueIndex] : null;

  /* Offline render handshake: publish the instant that is actually on screen, after the commit for
     it has painted. The capture waits for this to match the frame it asked for, so it can never
     screenshot the previous frame — which is what produced runs of identical frames, i.e. judder. */
  useEffect(() => {
    if (typeof window !== "undefined" && window.__TOUR_RENDER__) {
      document.body.dataset.tourT = elapsed.toFixed(4);
    }
  }, [elapsed]);

  const scene = cue?.scene || "fragments";
  /* Scene progress runs on TIME, not on which line is playing. Derived from the cue index it could
     only ever be 0 or 1 in a two-line scene, so anything staged between those values either popped
     in a single step or never appeared at all — which is why the fragments beat opened on a black
     frame and the film felt like it jumped rather than moved. */
  const sceneProgress = useMemo(() => {
    if (!data) return 0;
    const same = data.cues.filter((c) => c.scene === scene);
    if (!same.length) return 0;
    const start = same[0].start;
    const lastIdx = same[same.length - 1].i;
    const after = data.cues.find((c) => c.i === lastIdx + 1);
    const end = after ? after.start : (data.duration || start + 4);
    const span = Math.max(end - start, 0.6);
    return Math.min(1, Math.max(0, (time - start) / span));
  }, [data, scene, time]);

  /* Keep the previous scene mounted briefly so scene changes dissolve instead of cutting. */
  const [prevScene, setPrevScene] = useState(null);
  const sceneRef = useRef(scene);
  useEffect(() => {
    if (sceneRef.current === scene) return;
    setPrevScene(sceneRef.current);
    sceneRef.current = scene;
    const t = setTimeout(() => setPrevScene(null), 2000);
    return () => clearTimeout(t);
  }, [scene]);

  useEffect(() => {
    if (!playing) return;
  }, [scene, playing]);

  const play = useCallback(() => {
    setStarted(true);
    startNarration();
    if (!firedRef.current.has("started")) {
      firedRef.current.add("started");
      trackTour("started", 0);
    }
  }, [startNarration]);

  const pause = useCallback(() => {
    pauseNarration();
  }, [pauseNarration]);

  const toggle = useCallback(() => (playing ? pause() : play()), [playing, pause, play]);

  const seek = (seconds) => {
    if (!duration) return;
    seekNarration(Math.max(0, Math.min(seconds, duration - 0.2)));
  };

  const changeVolume = (v) => {
    setVolume(v);
    setMuted(v === 0);
    setClipVolume(v);
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setClipVolume(next ? 0 : volume);
  };

  /* The arrows: on a desktop browser this asks for real fullscreen (the browser chrome goes away);
     on iOS, where an element cannot go fullscreen, it drops into cinema mode instead, which hides
     the player's own chrome so the film runs edge to edge. Either way the screen visibly changes.
     The mode is mirrored into a ref because the keyboard listener is attached once: without it a
     quick second press evaluates against pre-toggle state and is swallowed. */
  const modeRef = useRef({ cinema: false, full: false });
  useEffect(() => { modeRef.current = { cinema, full }; }, [cinema, full]);

  const toggleFull = async () => {
    const node = shellRef.current;
    const live = modeRef.current;
    if (live.cinema || live.full || document.fullscreenElement || document.webkitFullscreenElement) {
      modeRef.current = { cinema: false, full: false };
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        try { await (document.exitFullscreen?.() || document.webkitExitFullscreen?.()); } catch { /* noop */ }
      }
      setFull(false);
      setCinema(false);
      return;
    }
    modeRef.current = { cinema: true, full: live.full };
    const request = node?.requestFullscreen || node?.webkitRequestFullscreen;
    if (request) {
      try {
        await request.call(node);
        setFull(true);
        setCinema(true);
        return;
      } catch { /* embedded or unsupported — cinema mode alone still fills the screen */ }
    }
    setCinema(true);
  };
  /* eslint-disable-next-line no-unused-vars */
  const expanded = full || cinema;

  const toggleFullRef = useRef(toggleFull);
  toggleFullRef.current = toggleFull;

  useEffect(() => {
    const keys = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const map = {
        " ": () => toggle(), k: () => toggle(),
        ArrowRight: () => seek(time + 10), ArrowLeft: () => seek(time - 10),
        m: toggleMute, c: () => setCaptionsOn((v) => !v), f: () => toggleFullRef.current(),
        Escape: () => (document.fullscreenElement ? null : onClose()),
      };
      const fn = map[e.key];
      if (fn) { e.preventDefault(); fn(); }
    };
    window.addEventListener("keydown", keys);
    return () => window.removeEventListener("keydown", keys);
  }, [toggle, time]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!duration) return;
    const pct = time / duration;
    MILESTONES.forEach(([at, event]) => {
      if (pct >= at && !firedRef.current.has(event)) {
        firedRef.current.add(event);
        trackTour(event, time);
      }
    });
  }, [time, duration]);

  /* Lock the page behind the player: without this, iOS rubber-bands the landing page underneath
     the tour while you are watching it. */
  useEffect(() => {
    const { overflow, position, width } = document.body.style;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = overflow;
      document.body.style.position = position;
      document.body.style.width = width;
    };
  }, []);

  const SceneView = SCENES[scene] || SCENES.fragments;
  const OutgoingView = prevScene && prevScene !== scene ? (SCENES[prevScene] || null) : null;

  return (
    <div ref={shellRef} data-testid="platform-tour"
         className="fixed inset-0 z-50 overflow-hidden bg-black" role="dialog"
         style={{ height: "100dvh" }}
         aria-label="Cornerstone Ops platform tour">
      <div data-testid="tour-rotor"
           className="absolute left-1/2 top-1/2 flex flex-col"
           style={{ width: box.w || "100%", height: box.h || "100%",
                    transform: `translate(-50%, -50%)${rotated ? " rotate(90deg)" : ""}`,
                    transformOrigin: "center center" }}>
      <div className="relative flex-1 min-h-0 overflow-hidden">
        {/* The cinematic frame: composed at 1600x900 and scaled to whatever screen it lands on */}
        <div ref={stageRef} data-testid="tour-stage"
             className="absolute inset-0 overflow-hidden bg-black">
        {!data && !failed && (
          <div className="absolute inset-0 flex items-center justify-center" data-testid="tour-loading">
            <Loader2 className="h-6 w-6 animate-spin text-gold" />
          </div>
        )}
        {failed && (
          <div className="absolute inset-0 flex items-center justify-center px-8 text-center"
               data-testid="tour-fallback">
            <div>
              <p className="font-display text-xl text-white font-semibold">
                The tour can't play on this connection
              </p>
              <p className="text-blue-100/70 text-sm mt-3 max-w-md">
                Cornerstone Ops brings scheduling, properties, teams, inspections, issues, vendors,
                invoices, payments and automated workflows into a single operational command center.
              </p>
              <button data-testid="tour-fallback-start" onClick={onStart}
                className="mt-6 h-11 rounded-full bg-gold px-7 font-display text-xs font-bold uppercase
                           tracking-[0.16em] text-royal-deep">
                Start With Cornerstone
              </button>
            </div>
          </div>
        )}
        {data && !failed && (
          <div data-testid="tour-frame"
               className="absolute left-1/2 top-1/2 overflow-hidden bg-royal-deep"
               style={{ width: frame.w, height: frame.h,
                        transform: `translate(-50%, -50%) scale(${scale})`,
                        transformOrigin: "center center" }}>
            {OutgoingView && (
              <div className="absolute inset-0 tour-scene-out" data-testid="tour-scene-out" aria-hidden>
                <OutgoingView progress={1} time={time} cues={data.cues} cueIndex={cueIndex} />
              </div>
            )}
            <div className="absolute inset-0 tour-scene-in" data-testid="tour-scene-in">
              <SceneView progress={sceneProgress} time={time} cues={data.cues} cueIndex={cueIndex}
                         onStart={onStart} onExplore={onExplore} />
            </div>
          </div>
        )}

        {data && !playing && !started && (
          <button data-testid="tour-big-play" onClick={play}
            className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-royal-deep/70
                       backdrop-blur-sm transition-opacity duration-300">
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-gold
                             tour-pulse-slow"><Play className="h-8 w-8 text-royal-deep ml-1" /></span>
            <span className="mt-5 font-display text-white text-lg sm:text-2xl font-semibold">
              Watch the Full Experience
            </span>
            <span className="mt-1.5 text-blue-100/70 text-xs tracking-[0.2em] uppercase">
              With sound · {clock(duration)}
            </span>
          </button>
        )}

        {recovering && (
          <div data-testid="tour-reconnecting"
               className="pointer-events-none absolute left-1/2 top-1/2 z-30 -translate-x-1/2
                          -translate-y-1/2 flex items-center gap-2.5 rounded-full bg-royal-deep/85
                          px-5 py-3 backdrop-blur-sm">
            <Loader2 className="h-4 w-4 animate-spin text-gold" />
            <span className="text-xs uppercase tracking-[0.18em] text-blue-100/80">
              Reconnecting · picking up where you left off
            </span>
          </div>
        )}

        <button data-testid="tour-close" onClick={onClose} aria-label="Close tour"
          className="absolute right-3 top-3 z-30 flex h-10 w-10 items-center justify-center rounded-full
                     bg-royal-deep/70 text-white backdrop-blur-sm transition-colors duration-200 hover:bg-royal-deep">
          <X className="h-4 w-4" />
        </button>
        {cinema && (
          <button data-testid="tour-exit-cinema" onClick={toggleFull} aria-label="Exit full screen"
            className="absolute right-14 top-3 z-30 flex h-10 w-10 items-center justify-center rounded-full
                       bg-royal-deep/70 text-white backdrop-blur-sm">
            <Minimize2 className="h-4 w-4" />
          </button>
        )}

        {captionsOn && cue && (speaking || !playing) && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-20 flex items-end justify-center px-3 pb-2 sm:pb-3"
               style={{ width: frame.w * scale, height: frame.h * scale,
                        transform: "translate(-50%, -50%)" }}
               data-testid="tour-caption" data-line-index={cueIndex}
               data-clip={cue.file?.split("/").pop()?.split("?")[0]}>
            <p className="max-w-[96%] sm:max-w-3xl bg-black px-3 py-2 text-center
                          text-[13px] leading-snug text-white
                          sm:px-4 sm:py-2.5 sm:text-base sm:font-medium">
              {cue.text}
            </p>
          </div>
        )}

        {/* When the player is rotated the film already fills the screen; tell the viewer to turn.
            It sits at the top so it never collides with a caption, and fades out on its own. */}
        {rotated && time < 8 && (
          <div data-testid="tour-rotate-hint"
               className="pointer-events-none absolute inset-x-0 top-4 z-20 flex justify-center">
            <span className="rounded-full bg-white/10 px-3 py-1.5 text-[10px] uppercase
                             tracking-[0.18em] text-blue-100/70">
              Turn your phone sideways
            </span>
          </div>
        )}
        </div>
      </div>

      {!cinema && (
      <div className="shrink-0 border-t border-white/10 bg-[#0b1330] px-3 sm:px-5 py-2.5 sm:py-3"
           style={{ paddingBottom: "max(0.625rem, env(safe-area-inset-bottom))" }}
           data-testid="tour-controls">
        <input data-testid="tour-progress" type="range" min={0} max={Math.max(duration, 1)} step={0.1}
          value={time} onChange={(e) => seek(Number(e.target.value))}
          aria-label="Playback progress"
          className="tour-range w-full" />
        <div className="mt-2.5 flex items-center gap-2 sm:gap-3">
          <button data-testid="tour-play-toggle" onClick={toggle} aria-label={playing ? "Pause" : "Play"}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold text-royal-deep">
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
          </button>
          <button data-testid="tour-restart" onClick={() => { seek(0); play(); }} aria-label="Restart"
            className={`h-10 w-10 items-center justify-center rounded-full text-blue-100/80
                       transition-colors duration-200 hover:text-gold
                       ${rotated ? "flex" : "hidden sm:flex"}`}>
            <RotateCcw className="h-4 w-4" />
          </button>
          <span className="text-[11px] sm:text-xs text-blue-100/70 tabular-nums whitespace-nowrap">
            {clock(time)} / {clock(duration)}
          </span>
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            <button data-testid="tour-mute" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}
              className="flex h-10 w-10 items-center justify-center rounded-full text-blue-100/80
                         transition-colors duration-200 hover:text-gold">
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <input data-testid="tour-volume" type="range" min={0} max={1} step={0.05}
              value={muted ? 0 : volume} onChange={(e) => changeVolume(Number(e.target.value))}
              aria-label="Volume" className={`tour-range w-20 ${rotated ? "block" : "hidden sm:block"}`} />
            <button data-testid="tour-captions" onClick={() => setCaptionsOn((v) => !v)}
              aria-pressed={captionsOn} aria-label="Toggle captions"
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-200
                          ${captionsOn ? "text-gold" : "text-blue-100/60 hover:text-gold"}`}>
              <Subtitles className="h-4 w-4" />
            </button>
            <button data-testid="tour-fullscreen" onClick={toggleFull} aria-label="Toggle full screen"
              className="flex h-10 w-10 items-center justify-center rounded-full text-blue-100/80
                         transition-colors duration-200 hover:text-gold">
              {full || cinema ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
      )}
      </div>
    </div>
  );
}
